import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import type { CrossTabConfig } from "./cross-tab/types";

export enum HttpStatusCode {
  OK = 200,
  CREATED = 201,
  ACCEPTED = 202,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  METHOD_NOT_ALLOWED = 405,
  CONFLICT = 409,
  GONE = 410,
  UNPROCESSABLE_ENTITY = 422,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504,
}

export enum ApiErrorType {
  NETWORK_ERROR = "NETWORK_ERROR",
  TIMEOUT_ERROR = "TIMEOUT_ERROR",
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  NOT_FOUND = "NOT_FOUND",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  RATE_LIMITED = "RATE_LIMITED",
  SERVER_ERROR = "SERVER_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  CANCELLED = "CANCELLED",
}

export interface ApiError {
  type: ApiErrorType;
  status?: number;
  message: string;
  code?: string;
  details?: Record<string, unknown>;
  originalError?: AxiosError;
}

export interface TokenStorage {
  getAccessToken: () => string | null | Promise<string | null>;
  getRefreshToken: () => string | null | Promise<string | null>;
  setTokens: (
    accessToken: string,
    refreshToken?: string,
  ) => void | Promise<void>;
  clearTokens: () => void | Promise<void>;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
}

export interface RetryConfig {
  count: number;
  delay: number;
  statusCodes: number[];
}
export interface InterceptorMetrics {
  onTokenRefreshStart?: () => void;
  onTokenRefreshSuccess?: (duration: number) => void;
  onTokenRefreshFailure?: (error: Error) => void;
  onRequestQueued?: (count: number) => void;
  onRequestRetry?: (attempt: number, statusCode: number) => void;
}

export interface HeaderRule {
  pattern: string | RegExp;
  add?: Record<
    string,
    string | (() => string | null) | (() => Promise<string | null>)
  >;
  remove?: string[];
  set?: Record<
    string,
    string | (() => string | null) | (() => Promise<string | null>)
  >;
  priority?: number;
}

export interface InterceptorConfig {
  tokenStorage?: TokenStorage;
  refreshTokenFn?: () => Promise<RefreshTokenResponse>;
  onAuthFailure?: () => void;
  onError?: (error: ApiError) => void;
  enableLogging?: boolean;
  enableLoggingInProduction?: boolean;
  customHeaders?: Record<string, string>;
  timeout?: number;
  baseURL?: string;
  retry?: RetryConfig;
  crossTab?: CrossTabConfig;
  metrics?: InterceptorMetrics;
  headerRules?: HeaderRule[];
}

export { type CrossTabConfig } from "./cross-tab/types";

export interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}
